import { Button, Modal, Tooltip } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2, ChevronRight, Repeat2, ShieldCheck, UserRound } from 'lucide-react';
import { useState } from 'react';
import { contextApi, getApiError, type AvailableContext } from '../lib/api.js';
import { refreshSession } from '../lib/session.js';
import {
  contextKey,
  contextSubtitle,
  contextTarget,
  contextTitle,
} from '../lib/session-contexts.js';

/// `compact` é o menu recolhido: só o ícone, com o nome na dica.
export function ContextSwitcher({ compact = false }: { compact?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState<string>();
  const [error, setError] = useState<string>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const contexts = useQuery({
    queryKey: ['auth', 'contexts'],
    queryFn: contextApi.list,
    enabled: open,
  });

  async function select(context: AvailableContext) {
    setSwitching(contextKey(context));
    setError(undefined);
    try {
      await contextApi.switch(contextTarget(context));
      await queryClient.invalidateQueries({ queryKey: ['auth', 'contexts'] });
      await refreshSession(queryClient);
      setOpen(false);
      // A área do cliente tem endereço próprio; os outros contextos passam pelo portão do início.
      await navigate({ to: context.kind === 'CUSTOMER' ? '/account' : '/home' });
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSwitching(undefined);
    }
  }

  return (
    <>
      {compact ? (
        <Tooltip content="Trocar contexto" side="right">
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Trocar contexto"
            className="grid size-10 cursor-pointer place-items-center rounded-xl border border-surface-border text-content-secondary transition hover:border-brand-500/50 hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            <Repeat2 className="size-4" aria-hidden="true" />
          </button>
        </Tooltip>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-surface-border text-xs font-medium text-content-secondary transition hover:border-brand-500/50 hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <Repeat2 className="size-4" />
          Trocar
        </button>
      )}
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Trocar contexto"
        description="Escolha um único ambiente de atuação. Os poderes não são combinados."
      >
        <div className="space-y-3">
          {error ? (
            <div
              role="alert"
              className="rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
            >
              {error}
            </div>
          ) : null}
          {contexts.isPending ? (
            <p className="text-sm text-content-muted">Carregando acessos…</p>
          ) : null}
          {contexts.isError ? (
            <p role="alert" className="text-sm text-danger">
              Não foi possível carregar os acessos.
            </p>
          ) : null}
          {contexts.data?.map((context) => {
            return (
              <button
                key={contextKey(context)}
                type="button"
                disabled={switching !== undefined}
                onClick={() => void select(context)}
                className="group flex w-full cursor-pointer items-center gap-3 rounded-xl border border-surface-border bg-surface-elevated px-4 py-3 text-left transition hover:border-brand-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-wait disabled:opacity-60"
              >
                <span className="grid size-9 place-items-center rounded-lg bg-brand-500/10 text-brand-400">
                  {context.kind === 'PLATFORM' ? (
                    <ShieldCheck className="size-4" />
                  ) : context.kind === 'CUSTOMER' ? (
                    <UserRound className="size-4" />
                  ) : (
                    <Building2 className="size-4" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <strong className="block truncate text-sm">
                    {context.kind === 'CUSTOMER' ? 'Área do cliente' : contextTitle(context)}
                  </strong>
                  <span className="block truncate text-xs text-content-faint">
                    {contextSubtitle(context)}
                  </span>
                </span>
                <ChevronRight className="size-4 text-content-faint transition group-hover:translate-x-0.5 group-hover:text-brand-400" />
              </button>
            );
          })}
          <div className="flex justify-end pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
